import React, { useContext, useEffect, useMemo, useState, useRef, useId, useLayoutEffect } from "react";
import { AuthContext } from "../context/AuthContext";
import { createPortal } from "react-dom";
import Config from "../utils/Config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatDateTime } from "../utils/functions";
import useTitle from '../hooks/useTitle';
import { toast } from "react-toastify";
import SearchBox from "./SearchBox";
import Pager from "./Pager";
import "./Users.css";
import InlineVerificationCode from "./VerifyCodePanel";
import EmailVerificationDialog from "./EmailVerificationDialog";

const PAGE_SIZES = [10, 25, 50];

const levelLabel = (lvl) => {
  if (lvl >= (Config?.USERS?.ADMIN_USER_LEVEL ?? 3)) return "Admin";
  if (lvl >= 2) return "Manager";
  return "User";
};

const roleBadgeKey = account => {
  const role = account.adminRole || levelLabel(account.userLevel);
  return ({'Super Admin':'super-admin','Operations Admin':'operations-admin','Support Admin':'support-admin','Billing Admin':'billing-admin','Content Admin':'content-admin',Admin:'super-admin',Manager:'operations-admin',User:'user'})[role] || 'user';
};

export function UserActionsMenu({ email, actions, onSelect }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({left:0,top:0});
  const trigger = useRef(null), menu = useRef(null), focusLast = useRef(false);
  const menuId = useId();
  const openMenu = last => {
    const rect=trigger.current.getBoundingClientRect(), width=Math.min(220,window.innerWidth-16), height=Math.min(actions.length*44+12,window.innerHeight-16);
    focusLast.current=last;
    setPosition({left:Math.max(8,Math.min(rect.right-width,window.innerWidth-width-8)),top:Math.max(8,rect.bottom+height+6<=window.innerHeight?rect.bottom+6:rect.top-height-6)});
    setOpen(true);
    if(open){const items=menu.current?.querySelectorAll('[role="menuitem"]');items?.[last?items.length-1:0]?.focus();}
  };
  useLayoutEffect(()=>{
    if(!open)return;
    const rect=trigger.current.getBoundingClientRect(), popup=menu.current.getBoundingClientRect();
    setPosition({left:Math.max(8,Math.min(rect.right-popup.width,window.innerWidth-popup.width-8)),top:rect.bottom+6+popup.height<=window.innerHeight-8?rect.bottom+6:Math.max(8,rect.top-popup.height-6)});
  },[open]);
  useEffect(()=>{
    if(!open)return;
    const items=menu.current.querySelectorAll('[role="menuitem"]');items[focusLast.current?items.length-1:0]?.focus();
    const outside=event=>{if(!trigger.current?.contains(event.target)&&!menu.current?.contains(event.target))setOpen(false);};
    const reposition=event=>{if(!(event.target instanceof Node&&menu.current?.contains(event.target)))setOpen(false);};
    document.addEventListener('pointerdown',outside);window.addEventListener('resize',reposition);window.addEventListener('scroll',reposition,true);
    return ()=>{document.removeEventListener('pointerdown',outside);window.removeEventListener('resize',reposition);window.removeEventListener('scroll',reposition,true);};
  },[open]);
  const keyDown=event=>{
    if(event.key==='Escape'||event.key==='Tab'){if(event.key==='Escape')event.preventDefault();setOpen(false);trigger.current.focus();return;}
    const items=[...menu.current.querySelectorAll('[role="menuitem"]')],index=items.indexOf(document.activeElement);
    const next={ArrowDown:(index+1)%items.length,ArrowUp:(index+items.length-1)%items.length,Home:0,End:items.length-1}[event.key];
    if(next!==undefined){event.preventDefault();items[next].focus();}
  };
  return <>
    <button ref={trigger} type="button" className="users-actions-trigger" aria-label={`Actions for ${email}`} aria-haspopup="menu" aria-expanded={open} aria-controls={open?menuId:undefined}
      onClick={()=>open?setOpen(false):openMenu(false)} onKeyDown={event=>{if(event.key==='ArrowDown'||event.key==='ArrowUp'){event.preventDefault();openMenu(event.key==='ArrowUp');}}}><span aria-hidden="true">⋮</span></button>
    {open&&createPortal(<div ref={menu} id={menuId} className="users-actions-menu" role="menu" aria-label={`Actions for ${email}`} style={position} onKeyDown={keyDown}>
      {actions.map(action=><button key={action.value} type="button" role="menuitem" tabIndex={-1} onClick={()=>{setOpen(false);trigger.current.focus();onSelect(action.value);}}>{action.label}</button>)}
    </div>,document.body)}
  </>;
}

const accountActionCopy = {
  role: { title: 'Change User Role', description: 'Choose the account role. The user will need to sign in again after this change.', confirm: 'Confirm role change' },
  suspend: { title: 'Suspend User', description: 'Suspend account access until an authorized administrator reactivates it. Existing sessions will be revoked.', confirm: 'Confirm suspension' },
  reactivate: { title: 'Reactivate User', description: 'Restore account access. The user must sign in again; previous sessions will remain invalid.', confirm: 'Confirm reactivation' },
  deactivate: { title: 'Deactivate User', description: 'Disable account access and revoke existing sessions. Dealership records and website content remain unchanged.', confirm: 'Confirm deactivation' },
  'request-deletion': { title: 'Request Reviewed Deletion', description: 'Submit this account for deletion review and deactivate its access. This does not permanently delete the account.', confirm: 'Confirm review request' },
  'approve-deletion-request': { title: 'Approve deletion review', description: 'Record approval for further deletion review. This action does not permanently delete the account or its records.', confirm: 'Confirm approval' },
  'reject-deletion-request': { title: 'Reject deletion request', description: 'Record that the deletion request has been declined. This does not reactivate the account.', confirm: 'Confirm rejection' },
};

  const fetchUsersList = async () => {
    const res = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.USERS_GET_ALL}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || sessionStorage.getItem('authToken')}` },
      cache: 'no-store',
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || 'Failed to load users');
    const records = Array.isArray(data) ? data : data.users;
    if (!Array.isArray(records)) throw new Error('Invalid users response.');
    return records;
  };

const Users = ({ mode = "admin" }) => {
  const isAdmin = mode === "admin";
  const { user } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [management, setManagement] = useState(null);
  const [managementBusy, setManagementBusy] = useState(false);
  const [managementError, setManagementError] = useState('');
  const managementFlight = useRef(false);
  const actionCopy = accountActionCopy[management?.action] || { title: 'Confirm account action', description: '', confirm: 'Confirm action' };
  const roles = ['Super Admin','Operations Admin','Support Admin','Billing Admin','Content Admin','User'];
  const openManagement = (target, action) => {setManagement({target,action,role:target.adminRole||'User',reason:'',password:'',code:'',codeDigits:Array(6).fill(''),challengeId:null});setManagementError('');};
  const managementRequest = async (verificationCode) => {
    if(managementFlight.current)return;
    managementFlight.current=true;setManagementBusy(true);setManagementError('');
    try {
      const suffix=management.challengeId?'complete':'start';
      const body=management.challengeId?{challengeId:management.challengeId,code:typeof verificationCode==='string'?verificationCode:management.code}:{targetId:management.target._id,action:management.action,role:management.role,reason:management.reason,password:management.password};
      const response=await fetch(`${Config.API.API_BASE_URL}/api/admin/account-management/${suffix}`,{method:'POST',headers:{Authorization:`Bearer ${localStorage.getItem('authToken')||sessionStorage.getItem('authToken')}`,'Content-Type':'application/json'},body:JSON.stringify(body)});
      const data=await response.json();if(!response.ok)throw new Error(data.message||'Account action failed.');
      if(suffix==='start'){setManagement(m=>({...m,password:'',challengeId:data.challengeId,expiresAt:data.expiresAt}));return;}
      // Completion returns an authority entry, not the normalized users-list record.
      // Close the consumed challenge before refreshing; a failed read must not retry the action.
      setManagement(null);toast.success('Account updated. Existing sessions were revoked.');
      try {
        const refreshed = await fetchUsersList();
        if (!refreshed.some(account => account._id === management.target._id)) throw new Error('Updated account missing from users response.');
        setUsers(refreshed);
      } catch {
        setErr('Account action completed, but the users list could not be refreshed. Reload Users to see the current account state.');
      }
    } catch(error){setManagementError(error.message);}finally{managementFlight.current=false;setManagementBusy(false);}
  };
  const managementActions = target => {
    const superAdmin=user?.adminRole==='Super Admin',support=user?.adminRole==='Support Admin',ordinary=(target.adminRole||'User')==='User';
    if(!superAdmin&&!(support&&ordinary))return null;
    const actions = [
      ...(superAdmin?[{value:'role',label:'Change role'}]:[]),
      {value:target.isActive?'suspend':'reactivate',label:target.isActive?'Suspend':'Reactivate'},
      ...(superAdmin?[{value:'deactivate',label:'Deactivate'},{value:'request-deletion',label:'Request reviewed deletion'}]:[]),
      ...(superAdmin&&target.deletionRequest?.status==='requested'?[{value:'approve-deletion-request',label:'Approve deletion review'},{value:'reject-deletion-request',label:'Reject deletion request'}]:[])
    ];
    return <UserActionsMenu email={target.email} actions={actions} onSelect={action=>openManagement(target,action)} />;
  };

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accountUser, setAccountUser] = useState();
  const [timeZone, setTimeZone] = useState("");
  const [timeZones, setTimeZones] = useState([]);
  const formRef = useRef(null);

  const passwordRef = useRef(null);
  const messageRef = useRef(null);

  // table UI state
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "createdAt", dir: "desc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    useMFA: user?.useMFA || false,
    timeZone: user?.timeZone || "",
    isActive: user?.isActive || true,
  });
  const [activePending, setActivePending] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);

  const [password, setPassword] = useState("");
  const [deactivateReason, setDeactivateReason] = useState("");
  const [passwordError, setPasswordError] = useState(null);
  const [cancelReason, setCancelReason] = useState("");

  const [showActiveModal, setShowActiveModal] = useState(false);

  const [mfaPending, setMfaPending] = useState(false);
  const [mfaModalOpen, setMfaModalOpen] = useState(false);
  const [mfaChallengeId, setMfaChallengeId] = useState(null);
  const [mfaExpiresAt, setMfaExpiresAt] = useState(null);
  const [mfaAction, setMfaAction] = useState(null); // 'enable' | 'disable'
  const [pendingMessage, setPendingMessage] = useState("");
  const [pendingStyle, setPendingStyle] = useState("warning");
  const [adminLevel, setAdminLevel] = useState("warning");
  const [messageClass, setMessageClass] = useState("hidden");
  const [challengeAction, setChallengeAction] = useState(null);

  // Reason options shown when DEACTIVATING a user
  const DEACTIVATE_REASON_OPTIONS = [
    ["", "Select reason"],
    ["left_company", "User left company"],
    ["temp_suspension", "Temporary suspension"],
    ["security_concern", "Security concern / compromised"],
    ["policy_violation", "Policy / TOS violation"],
    ["spam_abuse", "Spam / abuse"],
    ["duplicate", "Duplicate account"],
    ["not_verified", "Couldn't verify email"],
    ["by_request", "Deactivated on user request"],
    ["other", "Other"],
  ];

  const ADMIN_LEVEL = Config?.USERS?.ADMIN_USER_LEVEL ?? 3;

  useTitle('Users');

  // jump to top when paging (or page size/search changes)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [page, pageSize, q]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);



  // fetch users
  useEffect(() => {
    let isMounted = true;
    const fetchUsers = async () => {
      if (!user) return;
      setLoading(true);
      setErr(null);
      try {
        const records = await fetchUsersList();
        if (isMounted) setUsers(records);
      } catch (e) {
        if (isMounted) setErr(e.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchUsers();
    return () => { isMounted = false; };
  }, [user]);

  // time zones (for modal)
  useEffect(() => {
    const fetchTimeZones = async () => {
      try {
        const res = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_TIMEZONES}`);
        const data = await res.json();
        setTimeZones(data);

        const detectedZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const match = data.find((tz) => tz.value === detectedZone);
        if (match && !timeZone) setTimeZone(detectedZone);
      } catch (err) {
        console.error("❌ Error fetching timezones:", err);
      }
    };
    fetchTimeZones();
  }, []); // eslint-disable-line

  // filter + sort
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let rows = users;
    if (term) {
      rows = rows.filter((u) => {
        const name = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
        return (
          (u.email || "").toLowerCase().includes(term) ||
          name.includes(term) ||
          (u.timeZone || "").toLowerCase().includes(term)
        );
      });
    }
    const { key, dir } = sort;
    const compare = (a, b) => {
      const av = a?.[key];
      const bv = b?.[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;

      // date fields
      if (key === "createdAt" || key === "lastLoginAt") {
        return new Date(av) - new Date(bv);
      }
      if (typeof av === "number" && typeof bv === "number") return av - bv;
      return String(av).localeCompare(String(bv));
    };
    const sorted = [...rows].sort(compare);
    return dir === "desc" ? sorted.reverse() : sorted;
  }, [users, q, sort]);

  // pagination
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pageCount);
    const start = (safePage - 1) * pageSize;
  const rows = filtered.slice(start, start + pageSize);

  const totalCount = users.length;
  const shownCount = filtered.length;
  const hasQuery = q.trim().length > 0;

  const setSortKey = (key) => {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
  };

  // open account modal
  const openAccountModal = async (id) => {
    const response = await fetch(
      `${Config.API.API_BASE_URL}${Config.ENDPOINTS.USERS_GET_DETAILS}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("authToken")}`,
          "Accept": "application/json",
        },
        body: JSON.stringify({ id }),
      }
    );

    const data = await response.json();
    if (!response.ok) throw new Error(data?.message || "Failed to fetch user");
    setAccountUser(data.user);
    setShowAccountModal(true);
  };

  const closeAccountModal = () => setShowAccountModal(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Save actions here if needed
    } catch {
      toast.error("❌ Server error.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const shown = filtered.length;

  return (
    <div className="login-container login-container-dashboard">
      <div className="login-box" id="admin-users">
        <header className="claims-h claims-header fflClaims-search-box">
          <h2>
            <span className="titleIcon"><FontAwesomeIcon icon={["fas", "users"]} /></span>&nbsp;&nbsp;Users
          </h2>
          <SearchBox
            filteredClaims={filtered}
            onChange={value => { setQ(value); setPage(1); }}
            placeholder="Search users..."
            ariaLabel="Search users"
            shown={shown}
            total={totalCount}
            hasQuery={hasQuery}
            query={q}
            setQuery={value => { setQ(value); setPage(1); }}
            pageSize={pageSize}
            setPageSize={setPageSize}
            setPage={setPage}
          />
        </header>

        <div className="admin-table-controls users-tools mobile-search-box">
          <div className="admin-table-right">
            <label className="rows-label">Rows:</label>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="rows-select"
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Desktop Table */}
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th onClick={() => setSortKey("email")} className="sortable">
                  Email {sort.key === "email" && (sort.dir === "asc" ? "▲" : "▼")}
                </th>
                <th onClick={() => setSortKey("firstName")} className="sortable">
                  Name {sort.key === "firstName" && (sort.dir === "asc" ? "▲" : "▼")}
                </th>
                <th onClick={() => setSortKey("userLevel")} className="sortable center">
                  Role {sort.key === "userLevel" && (sort.dir === "asc" ? "▲" : "▼")}
                </th>
                <th className="center">MFA</th>
                <th onClick={() => setSortKey("isActive")} className="sortable">
                  Active {sort.key === "isActive" && (sort.dir === "asc" ? "▲" : "▼")}
                </th>
                <th>Actions</th>
                <th onClick={() => setSortKey("lastLoginAt")} className="sortable">
                  Last Login {sort.key === "lastLoginAt" && (sort.dir === "asc" ? "▲" : "▼")}
                </th>
                <th onClick={() => setSortKey("createdAt")} className="sortable">
                  Created {sort.key === "createdAt" && (sort.dir === "asc" ? "▲" : "▼")}
                </th>
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="center dimmed">Loading…</td>
                </tr>
              )}
              {err && !loading && (
                <tr>
                  <td colSpan={8} className="center error">{err}</td>
                </tr>
              )}
              {!loading && !err && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="center dimmed">No users found.</td>
                </tr>
              )}
              {!loading && !err && rows.map((u) => {
                const name = [u.firstName, u.lastName].filter(Boolean).join(" ") || "—";
                return (
                  <tr key={u._id}>
                    <td
                      className="mono clickable"
                      onClick={() => openAccountModal(u._id)}
                      style={{ cursor: "pointer", color: "#007bff" }}
                    >
                      {u.email}
                    </td>
                    <td>{name}</td>
                    <td className="center">
                      <span className={`badge role ${roleBadgeKey(u)}`}>
                        {u.adminRole || levelLabel(u.userLevel)}
                      </span>
                    </td>
                    <td className="center">
                      <span className={`badge ${u.useMFA ? "ok" : "muted"}`}>
                        {u.adminRole && u.adminRole !== 'User' ? 'Required' : (u.useMFA ? 'On' : 'Off')}
                      </span>
                    </td>
                    <td className="center">
                      {(u.userLevel ?? 0) >= ADMIN_LEVEL ? (
                        <span
                          className={`badge ${u.isActive ? "ok" : "warn"}`}
                          style={{ cursor: "default", pointerEvents: "none" }}
                          title="Use Actions to manage administrator status"
                        >
                          {u.accountStatus || (u.isActive ? 'active' : 'deactivated')}
                        </span>
                      ) : (
                        <span
                          className={`badge ${u.isActive ? "ok" : "warn"}`}
                          onClick={() => openManagement(u, u.isActive?'suspend':'reactivate')}
                          style={{ cursor: "pointer" }}
                          title="Click to toggle active status"
                        >
                          {u.accountStatus || (u.isActive ? 'active' : 'deactivated')}
                        </span>
                      )}
                    </td>
                    <td>{managementActions(u)}</td>
                    <td>{u.lastLoginAt ? formatDateTime(u.lastLoginAt) : "—"}</td>
                    <td>{u.createdAt ? formatDateTime(u.createdAt) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="users-cards">
          {rows.map((u) => {
            const name = [u.firstName, u.lastName].filter(Boolean).join(" ") || "—";
            const isAdminRole = (u.userLevel ?? 0) >= ADMIN_LEVEL;

            return (
              <article key={u._id} className="user-card">
                <header className="uc-head">
                  <div className="uc-label">Email</div>
                  <a
                    className="uc-value uc-email"
                    onClick={() => openAccountModal(u._id)}
                    href="#!"
                    role="button"
                  >
                    {u.email}
                  </a>
                </header>

                <div className="uc-row">
                  <div className="uc-label">Name</div>
                  <div className="uc-value">{name}</div>
                </div>

                <div className="uc-row">
                  <div className="uc-label">Role</div>
                  <div className="uc-value">
                    <span className={`badge role ${roleBadgeKey(u)}`}>
                      {u.adminRole || levelLabel(u.userLevel)}
                    </span>
                  </div>
                </div>

                <div className="uc-row">
                  <div className="uc-label">MFA</div>
                  <div className="uc-value">
                    <span className={`badge ${u.useMFA ? "ok" : "muted"}`}>
                      {u.adminRole && u.adminRole !== 'User' ? 'Required' : (u.useMFA ? 'On' : 'Off')}
                    </span>
                  </div>
                </div>

                <div className="uc-row">
                  <div className="uc-label">Active</div>
                  <div className="uc-value">
                    {isAdminRole ? (
                      <span className={`badge ${u.isActive ? "ok" : "warn"}`} title="Use Actions to manage administrator status">
                        {u.accountStatus || (u.isActive ? 'active' : 'deactivated')}
                      </span>
                    ) : (
                      <button
                        type="button"
                        className={`pill ${u.isActive ? "ok" : "warn"}`}
                        onClick={() => openManagement(u, u.isActive?'suspend':'reactivate')}
                        title="Toggle active"
                      >
                        {u.accountStatus || (u.isActive ? 'active' : 'deactivated')}
                      </button>
                    )}
                  </div>
                </div>

                <div className="uc-row">
                  <div className="uc-label">Actions</div><div className="uc-value">{managementActions(u)}</div></div><div className="uc-row">
                  <div className="uc-label">Last Login</div>
                  <div className="uc-value">{u.lastLoginAt ? formatDateTime(u.lastLoginAt) : "—"}</div>
                </div>

                <div className="uc-row">
                  <div className="uc-label">Created</div>
                  <div className="uc-value">{u.createdAt ? formatDateTime(u.createdAt) : "—"}</div>
                </div>
              </article>
            );
          })}
        </div>

        {/* Pagination */}
        <Pager
          safePage={safePage}
          setPage={setPage}
          pageCount={pageCount}
        />
      </div>

      {/* Account Modal */}
      {showAccountModal && (
        <div className="modal-overlay">
          <div className="modal modal-users">
            <h3><FontAwesomeIcon icon="user" />&nbsp;&nbsp;Account Details</h3>

            <form ref={formRef} onSubmit={handleSubmit}>
              <label>
                <div className="account-fields-row">
                  <div className="account-field account-field-users">
                    <input type="text" placeholder="First Name" value={accountUser.firstName} readOnly />
                  </div>

                  <div className="account-field account-field-users">
                    <input type="text" placeholder="Last Name" value={accountUser.lastName} readOnly />
                  </div>

                  <div className="account-field account-field-users">
                    <input type="email" placeholder="Email" readOnly name="email" value={accountUser.email} disabled />
                  </div>

                  <div className="account-field account-field-users">
                    <select name="timeZone" className="selectCtrl" value={formData.timeZone} disabled>
                      <option value="">Select Time Zone</option>
                      {timeZones.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          (UTC {tz.offset}) {tz.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </label>
            </form>

            <div className="modal-buttons modal-buttons-users">
              <button className="confirm-delete" onClick={closeAccountModal}>Cancel</button>
              <button className="cancel-delete" onClick={handleSubmit}>Save and Close</button>
            </div>
          </div>
        </div>
      )}

      {management && !management.challengeId && <div className="users-account-action-overlay"><div className="users-account-action-dialog" role="dialog" aria-modal="true" aria-labelledby="account-management-title" aria-describedby="account-management-description">
        <h3 id="account-management-title">{actionCopy.title}</h3>
        <p className="users-account-action-target">{management.target.email}</p>
        <p id="account-management-description">{actionCopy.description}</p>
        {management.action==='request-deletion'&&<p className="users-account-action-notice">Deletion requests require review. Dealership claims, directory listings, payments, website content and records required for legal or operational purposes are preserved.</p>}
          {management.action==='role'&&<div className="users-account-action-field"><label htmlFor="account-action-role">Account role</label><select id="account-action-role" value={management.role} onChange={e=>setManagement(m=>({...m,role:e.target.value}))}>{roles.map(role=><option key={role}>{role}</option>)}</select></div>}
          <div className="users-account-action-field"><label htmlFor="account-action-reason">Reason</label><textarea id="account-action-reason" rows={3} maxLength={500} value={management.reason} onChange={e=>setManagement(m=>({...m,reason:e.target.value}))}/></div>
          <div className="users-account-action-field"><label htmlFor="account-action-password">Administrator password</label><input id="account-action-password" type="password" autoComplete="current-password" value={management.password} onChange={e=>setManagement(m=>({...m,password:e.target.value}))}/></div>
        {managementError&&<p role="alert">{managementError}</p>}
        <div className="users-account-action-buttons">
          <button className="users-account-action-cancel" type="button" disabled={managementBusy} onClick={()=>setManagement(null)}>Cancel</button>
          <button className="users-account-action-submit" type="button" disabled={managementBusy||(!management.challengeId?!management.password:management.code.length!==6)} onClick={managementRequest}>{management.challengeId?actionCopy.confirm:'Send confirmation code'}</button>
        </div>
      </div></div>}
      {management?.challengeId && <EmailVerificationDialog title={{role:'Verify Role Change',suspend:'Verify Suspend User',reactivate:'Verify Reactivate User',deactivate:'Verify Deactivate User','request-deletion':'Verify Deletion Request','approve-deletion-request':'Verify Review Approval','reject-deletion-request':'Verify Review Rejection'}[management.action] || 'Verify Account Action'}>
        <InlineVerificationCode
          parent="account-management"
          autoSubmit={true}
          allowResend={false}
          busy={managementBusy}
          onSubmit={managementRequest}
          onClose={() => setManagement(null)}
          email={user.email}
          messageRef={messageRef}
          messageClass={messageClass}
          setMessageClass={setMessageClass}
          pendingMessage={`Verification code sent. Confirm this action for ${management.target.email}.`}
          errorMessage={managementError}
          codeExpiresAt={management.expiresAt}
        />
      </EmailVerificationDialog>}
      {isSubmitting && (
        <div className="fullscreen-mask">
          <div className="spinner-container">
            <div className="spinner" />
            <p>Submitting. Please wait...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;